import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CalendarClock,
  Copy,
  Edit3,
  Percent,
  Plus,
  RefreshCw,
  Route,
  Trash2,
  Truck,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { FilterPanel, SortableHeader } from "../../components/data-controls";
import { LiaDateTimeField } from "../../components/date-picker";
import {
  ActionMenu,
  ActionMenuItem,
  Badge,
  Button,
  Confirm,
  DataTableShell,
  EmptyState,
  ErrorState,
  FormErrorSummary,
  FormSection,
  IconButton,
  Input,
  Loading,
  Modal,
  Notice,
  PageHeader,
  Pagination,
  SearchBox,
  Select,
  Switch,
  Textarea,
} from "../../components/ui";
import { adminApi } from "../../lib/api";
import {
  couponSchema,
  shippingSchema,
  type CouponFormValues,
  type ShippingFormValues,
} from "../../lib/form-schemas";
import { dateTime, money, number } from "../../lib/format";
import { labelOf } from "../../lib/labels";
import type { AdminCoupon, AdminShippingMethod } from "../../types/api";

const PAGE_SIZES = [5, 10, 20, 50, 100];

const newCoupon = (): CouponFormValues => ({
  code: "",
  description: "",
  discountType: "Percentage",
  amount: 10,
  maximumDiscountAmount: null,
  minimumOrderAmount: 0,
  startsAt: new Date().toISOString(),
  endsAt: new Date(Date.now() + 30 * 864e5).toISOString(),
  usageLimit: null,
  perUserLimit: 1,
  isActive: true,
});

const cleanCode = (value: string) => value.toUpperCase().replace(/[^A-Z0-9_-]/g, "");
const randomCouponCode = () => `LIA-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;

function couponState(coupon: AdminCoupon) {
  const now = Date.now();
  if (!coupon.isActive) return { key: "disabled", label: "غیرفعال", tone: "neutral" as const };
  if (new Date(coupon.startsAt).getTime() > now) return { key: "scheduled", label: "زمان‌بندی‌شده", tone: "warning" as const };
  if (new Date(coupon.endsAt).getTime() < now) return { key: "expired", label: "منقضی", tone: "danger" as const };
  if (coupon.usageLimit && coupon.usedCount >= coupon.usageLimit) return { key: "finished", label: "سقف مصرف تکمیل", tone: "danger" as const };
  return { key: "active", label: "فعال", tone: "success" as const };
}

export function CouponsPage() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ["coupons"], queryFn: adminApi.coupons.list });
  const [editing, setEditing] = useState<AdminCoupon | "new" | null>(null);
  const [deleting, setDeleting] = useState<AdminCoupon | null>(null);
  const [search, setSearch] = useState("");
  const [state, setState] = useState("all");
  const [kind, setKind] = useState("all");
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const {
    register,
    control,
    reset,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<CouponFormValues>({
    resolver: zodResolver(couponSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: newCoupon(),
  });

  const discountType = useWatch({ control, name: "discountType" });
  const amount = useWatch({ control, name: "amount" });
  const maximumDiscountAmount = useWatch({ control, name: "maximumDiscountAmount" });
  const minimumOrderAmount = useWatch({ control, name: "minimumOrderAmount" });

  const open = (item: AdminCoupon | "new") => {
    setEditing(item);
    reset(item === "new" ? newCoupon() : {
      code: item.code,
      description: item.description || "",
      discountType: item.discountType,
      amount: item.amount,
      maximumDiscountAmount: item.maximumDiscountAmount ?? null,
      minimumOrderAmount: item.minimumOrderAmount,
      startsAt: item.startsAt,
      endsAt: item.endsAt,
      usageLimit: item.usageLimit ?? null,
      perUserLimit: item.perUserLimit ?? null,
      isActive: item.isActive,
    });
  };

  const save = useMutation({
    mutationFn: (form: CouponFormValues): any => editing === "new"
      ? adminApi.coupons.create(form)
      : adminApi.coupons.update((editing as AdminCoupon).id, form),
    onSuccess: async () => {
      setEditing(null);
      await queryClient.invalidateQueries({ queryKey: ["coupons"] });
    },
  });

  const remove = useMutation({
    mutationFn: adminApi.coupons.remove,
    onSuccess: async () => {
      setDeleting(null);
      await queryClient.invalidateQueries({ queryKey: ["coupons"] });
    },
  });

  const items = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    const data = (query.data || [])
      .filter((item) => !keyword || `${item.code} ${item.description || ""}`.toLowerCase().includes(keyword))
      .filter((item) => state === "all" || couponState(item).key === state)
      .filter((item) => kind === "all" || item.discountType === kind);

    return [...data].sort((a, b) => {
      if (sort === "code-asc") return a.code.localeCompare(b.code);
      if (sort === "code-desc") return b.code.localeCompare(a.code);
      if (sort === "amount-desc") return b.amount - a.amount;
      if (sort === "amount-asc") return a.amount - b.amount;
      if (sort === "usage-desc") return b.usedCount - a.usedCount;
      if (sort === "usage-asc") return a.usedCount - b.usedCount;
      if (sort === "ending") return new Date(a.endsAt).getTime() - new Date(b.endsAt).getTime();
      if (sort === "oldest") return new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime();
      return new Date(b.startsAt).getTime() - new Date(a.startsAt).getTime();
    });
  }, [kind, query.data, search, sort, state]);

  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const visible = items.slice((safePage - 1) * pageSize, safePage * pageSize);
  const activeFilters = [search, state !== "all" ? state : "", kind !== "all" ? kind : ""].filter(Boolean).length;
  const changeSort = (value: string) => { setSort(value); setPage(1); };

  return (
    <>
      <PageHeader
        title="کدهای تخفیف"
        description="کمپین‌های تخفیف را با کد انگلیسی استاندارد، محدودیت مصرف و زمان‌بندی دقیق مدیریت کنید."
        actions={<Button onClick={() => open("new")}><Plus size={17} /> کد تخفیف جدید</Button>}
      />

      <FilterPanel
        search={<SearchBox value={search} onChange={(value) => { setSearch(value); setPage(1); }} placeholder="جست‌وجوی کد یا توضیحات..." />}
        activeCount={activeFilters}
        totalLabel={`${number(items.length)} کد`}
        onClear={() => { setSearch(""); setState("all"); setKind("all"); setSort("newest"); setPage(1); }}
      >
        <Select label="وضعیت" value={state} onChange={(event) => { setState(event.target.value); setPage(1); }}>
          <option value="all">همه وضعیت‌ها</option>
          <option value="active">فعال</option>
          <option value="scheduled">زمان‌بندی‌شده</option>
          <option value="expired">منقضی‌شده</option>
          <option value="finished">سقف مصرف تکمیل</option>
          <option value="disabled">غیرفعال</option>
        </Select>
        <Select label="نوع تخفیف" value={kind} onChange={(event) => { setKind(event.target.value); setPage(1); }}>
          <option value="all">همه انواع</option>
          <option value="Percentage">درصدی</option>
          <option value="FixedAmount">مبلغ ثابت</option>
        </Select>
        <Select label="مرتب‌سازی" value={sort} onChange={(event) => changeSort(event.target.value)}>
          <option value="newest">جدیدترین شروع</option>
          <option value="oldest">قدیمی‌ترین شروع</option>
          <option value="ending">نزدیک‌ترین پایان</option>
          <option value="usage-desc">بیشترین استفاده</option>
          <option value="amount-desc">بیشترین مقدار</option>
          <option value="code-asc">کد: A تا Z</option>
        </Select>
      </FilterPanel>

      {query.isLoading ? <Loading /> : query.error ? <ErrorState error={query.error} retry={() => void query.refetch()} /> : !visible.length ? (
        <EmptyState title="کد تخفیفی پیدا نشد" />
      ) : (
        <DataTableShell pagination={<Pagination page={safePage} totalPages={totalPages} totalCount={items.length} pageSize={pageSize} onChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1); }} pageSizeOptions={PAGE_SIZES} />}>
          <table className="product-table management-table">
            <thead>
              <tr>
                <SortableHeader label="کد" value={sort} ascValue="code-asc" descValue="code-desc" onChange={changeSort} />
                <th>وضعیت</th>
                <th>نوع</th>
                <SortableHeader label="مقدار" value={sort} ascValue="amount-asc" descValue="amount-desc" defaultDirection="desc" onChange={changeSort} />
                <SortableHeader label="مصرف" value={sort} ascValue="usage-asc" descValue="usage-desc" defaultDirection="desc" onChange={changeSort} />
                <th>بازه اجرا</th>
                <th>عملیات</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((coupon) => {
                const currentState = couponState(coupon);
                return (
                  <tr key={coupon.id}>
                    <td data-label="کد تخفیف">
                      <div className="coupon-status">
                        <strong className="coupon-code" dir="ltr">{coupon.code}</strong>
                        <small>{coupon.description || "بدون توضیح"}</small>
                      </div>
                    </td>
                    <td data-label="وضعیت"><Badge value={currentState.label} tone={currentState.tone} /></td>
                    <td data-label="نوع">{labelOf(coupon.discountType)}</td>
                    <td data-label="مقدار"><strong>{coupon.discountType === "Percentage" ? `${number(coupon.amount)}٪` : money(coupon.amount)}</strong></td>
                    <td data-label="مصرف">{number(coupon.usedCount)}{coupon.usageLimit ? ` از ${number(coupon.usageLimit)}` : " بار"}</td>
                    <td data-label="بازه اجرا"><div className="cell-stack"><strong>{dateTime(coupon.startsAt)}</strong><small>تا {dateTime(coupon.endsAt)}</small></div></td>
                    <td data-label="عملیات">
                      <ActionMenu>
                        <ActionMenuItem onClick={() => open(coupon)}><Edit3 size={15} /> ویرایش</ActionMenuItem>
                        <ActionMenuItem danger onClick={() => setDeleting(coupon)}><Trash2 size={15} /> حذف</ActionMenuItem>
                      </ActionMenu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </DataTableShell>
      )}

      <Modal open={Boolean(editing)} title={editing === "new" ? "تعریف کد تخفیف" : "ویرایش کد تخفیف"} onClose={() => setEditing(null)} wide>
        <form className="stack" onSubmit={handleSubmit((form) => save.mutate(form))} noValidate>
          <FormErrorSummary errors={[errors.code?.message, errors.amount?.message, errors.minimumOrderAmount?.message, errors.startsAt?.message, errors.endsAt?.message, errors.usageLimit?.message, errors.perUserLimit?.message]} />

          <FormSection
            title="اطلاعات اصلی"
            description="کد تخفیف فقط با حروف بزرگ انگلیسی، عدد، خط تیره یا زیرخط ذخیره می‌شود."
            action={<Button type="button" variant="ghost" onClick={() => setValue("code", randomCouponCode(), { shouldDirty: true, shouldValidate: true })}><RefreshCw size={15} /> تولید کد</Button>}
          >
            <div className="form-grid form-grid--3">
              <Input label="کد تخفیف" dir="ltr" autoComplete="off" error={errors.code?.message} hint="مثال: LIA20 یا SUMMER_2026" {...register("code", { onChange: (event) => setValue("code", cleanCode(String(event.target.value)), { shouldDirty: true, shouldValidate: true }) })} />
              <Select label="نوع تخفیف" hint="درصدی یا مبلغ ثابت به تومان." {...register("discountType")}>
                <option value="Percentage">درصدی</option>
                <option value="FixedAmount">مبلغ ثابت</option>
              </Select>
              <Input label={discountType === "Percentage" ? "درصد تخفیف" : "مبلغ تخفیف (تومان)"} type="number" min="1" step="1" error={errors.amount?.message} {...register("amount", { valueAsNumber: true })} />
              <Textarea className="span-full" label="توضیح داخلی کمپین" rows={3} error={errors.description?.message} hint="برای تشخیص کمپین در پنل؛ این متن الزاماً در سایت نمایش داده نمی‌شود." {...register("description")} />
            </div>
          </FormSection>

          <FormSection title="محدودیت مالی" description="حداقل خرید و سقف تخفیف را برای کنترل حاشیه سود مشخص کنید.">
            <div className="form-grid">
              <Input label="حداقل مبلغ سفارش (تومان)" type="number" min="0" error={errors.minimumOrderAmount?.message} hint="صفر یعنی بدون حداقل خرید." {...register("minimumOrderAmount", { valueAsNumber: true })} />
              <Input label="حداکثر مبلغ تخفیف (تومان)" type="number" min="1" error={errors.maximumDiscountAmount?.message} hint={discountType === "Percentage" ? "برای تخفیف درصدی توصیه می‌شود." : "در تخفیف ثابت معمولاً خالی بماند."} {...register("maximumDiscountAmount", { setValueAs: (value) => value === "" ? null : Number(value) })} />
            </div>
          </FormSection>

          <FormSection title="محدودیت استفاده" description="سقف کل مصرف و سقف مصرف هر مشتری را تعیین کنید.">
            <div className="form-grid">
              <Input label="سقف کل استفاده" type="number" min="1" error={errors.usageLimit?.message} hint="خالی یعنی بدون محدودیت کل." {...register("usageLimit", { setValueAs: (value) => value === "" ? null : Number(value) })} />
              <Input label="سقف استفاده هر مشتری" type="number" min="1" error={errors.perUserLimit?.message} hint="مثلاً ۱ یعنی هر مشتری فقط یک بار." {...register("perUserLimit", { setValueAs: (value) => value === "" ? null : Number(value) })} />
            </div>
          </FormSection>

          <FormSection title="زمان‌بندی و وضعیت" description="شروع و پایان کمپین را دقیق مشخص کنید؛ وضعیت نهایی از زمان و فعال‌بودن کد محاسبه می‌شود.">
            <div className="form-grid">
              <Controller
                control={control}
                name="startsAt"
                render={({ field }) => (
                  <LiaDateTimeField
                    label="شروع کمپین"
                    value={field.value}
                    error={errors.startsAt?.message}
                    hint="تقویم شمسی و ساعت رسمی تهران."
                    onChange={field.onChange}
                    required
                  />
                )}
              />
              <Controller
                control={control}
                name="endsAt"
                render={({ field }) => (
                  <LiaDateTimeField
                    label="پایان کمپین"
                    value={field.value}
                    error={errors.endsAt?.message}
                    hint="پایان باید بعد از زمان شروع کمپین باشد."
                    onChange={field.onChange}
                    required
                  />
                )}
              />
              <div className="span-2"><Controller control={control} name="isActive" render={({ field }) => <Switch label="کد فعال" checked={field.value} hint="خاموش‌کردن این گزینه، کمپین را بدون حذف متوقف می‌کند." onChange={field.onChange} />} /></div>
            </div>
          </FormSection>

          <div className="shipping-preview">
            <strong>پیش‌نمایش قانون تخفیف</strong>
            <span>{discountType === "Percentage" ? `${number(amount || 0)}٪ تخفیف` : money(amount || 0)}{maximumDiscountAmount ? ` تا سقف ${money(maximumDiscountAmount)}` : ""}{minimumOrderAmount ? ` برای سفارش‌های بالای ${money(minimumOrderAmount)}` : ""}</span>
          </div>

          {save.error ? <Notice tone="warning">{save.error.message}</Notice> : null}
          <div className="form-actions">
            <Button type="button" variant="ghost" onClick={() => setEditing(null)}>انصراف</Button>
            <Button type="submit" busy={save.isPending}>ذخیره کد تخفیف</Button>
          </div>
        </form>
      </Modal>

      <Confirm open={Boolean(deleting)} title="حذف کد تخفیف" description={`کد «${deleting?.code || ""}» حذف شود؟ سوابق سفارش‌های قبلی مستقل از این عملیات باقی می‌مانند.`} confirmLabel="حذف کد" busy={remove.isPending} onClose={() => setDeleting(null)} onConfirm={() => deleting && remove.mutate(deleting.id)} />
    </>
  );
}

const newShipping = (): ShippingFormValues => ({
  code: "",
  name: "",
  description: "",
  basePrice: 0,
  freeShippingThreshold: null,
  estimatedMinDays: 1,
  estimatedMaxDays: 3,
  isActive: true,
  displayOrder: 0,
});

export function ShippingPage() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ["shipping-methods"], queryFn: adminApi.shipping.list });
  const [editing, setEditing] = useState<AdminShippingMethod | "new" | null>(null);
  const [deleting, setDeleting] = useState<AdminShippingMethod | null>(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"" | "active" | "inactive">("");
  const [sort, setSort] = useState("order");

  const {
    register,
    control,
    reset,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ShippingFormValues>({ resolver: zodResolver(shippingSchema), mode: "onBlur", reValidateMode: "onChange", defaultValues: newShipping() });

  const basePrice = useWatch({ control, name: "basePrice" });
  const threshold = useWatch({ control, name: "freeShippingThreshold" });
  const minDays = useWatch({ control, name: "estimatedMinDays" });
  const maxDays = useWatch({ control, name: "estimatedMaxDays" });
  const active = useWatch({ control, name: "isActive" });
  const name = useWatch({ control, name: "name" });

  const open = (item: AdminShippingMethod | "new") => {
    setEditing(item);
    reset(item === "new" ? newShipping() : {
      code: item.code,
      name: item.name,
      description: item.description || "",
      basePrice: item.basePrice,
      freeShippingThreshold: item.freeShippingThreshold ?? null,
      estimatedMinDays: item.estimatedMinDays,
      estimatedMaxDays: item.estimatedMaxDays,
      isActive: item.isActive,
      displayOrder: item.displayOrder,
    });
  };

  const save = useMutation({
    mutationFn: (form: ShippingFormValues): any => editing === "new" ? adminApi.shipping.create(form) : adminApi.shipping.update((editing as AdminShippingMethod).id, form),
    onSuccess: async () => { setEditing(null); await queryClient.invalidateQueries({ queryKey: ["shipping-methods"] }); },
  });
  const remove = useMutation({
    mutationFn: adminApi.shipping.remove,
    onSuccess: async () => { setDeleting(null); await queryClient.invalidateQueries({ queryKey: ["shipping-methods"] }); },
  });
  const toggleActive = useMutation({
    mutationFn: (item: AdminShippingMethod) => adminApi.shipping.update(item.id, {
      code: item.code,
      name: item.name,
      description: item.description || null,
      basePrice: item.basePrice,
      freeShippingThreshold: item.freeShippingThreshold ?? null,
      estimatedMinDays: item.estimatedMinDays,
      estimatedMaxDays: item.estimatedMaxDays,
      isActive: !item.isActive,
      displayOrder: item.displayOrder,
    }),
    onSuccess: async () => { await queryClient.invalidateQueries({ queryKey: ["shipping-methods"] }); },
  });

  const items = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    const data = (query.data || [])
      .filter((item) => !keyword || `${item.name} ${item.code} ${item.description || ""}`.toLowerCase().includes(keyword))
      .filter((item) => !status || (status === "active" ? item.isActive : !item.isActive));
    return [...data].sort((a, b) => {
      if (sort === "name") return a.name.localeCompare(b.name, "fa");
      if (sort === "price-desc") return b.basePrice - a.basePrice;
      if (sort === "price-asc") return a.basePrice - b.basePrice;
      return a.displayOrder - b.displayOrder || a.name.localeCompare(b.name, "fa");
    });
  }, [query.data, search, sort, status]);

  return (
    <>
      <PageHeader title="روش‌های ارسال" description="هزینه، زمان تحویل، آستانه ارسال رایگان و ترتیب نمایش روش‌های ارسال را مدیریت کنید." actions={<Button onClick={() => open("new")}><Plus size={17} /> روش ارسال جدید</Button>} />

      <FilterPanel
        search={<SearchBox value={search} onChange={setSearch} placeholder="جست‌وجوی نام، کد یا توضیحات..." />}
        activeCount={[search, status].filter(Boolean).length}
        totalLabel={`${number(items.length)} روش`}
        onClear={() => { setSearch(""); setStatus(""); setSort("order"); }}
      >
        <Select label="وضعیت" value={status} onChange={(event) => setStatus(event.target.value as typeof status)}>
          <option value="">همه وضعیت‌ها</option>
          <option value="active">فعال</option>
          <option value="inactive">غیرفعال</option>
        </Select>
        <Select label="مرتب‌سازی" value={sort} onChange={(event) => setSort(event.target.value)}>
          <option value="order">ترتیب نمایش</option>
          <option value="name">نام</option>
          <option value="price-asc">کمترین هزینه</option>
          <option value="price-desc">بیشترین هزینه</option>
        </Select>
      </FilterPanel>

      {query.isLoading ? <Loading /> : query.error ? <ErrorState error={query.error} retry={() => void query.refetch()} /> : !items.length ? <EmptyState title="روش ارسالی پیدا نشد" /> : (
        <div className="shipping-grid">
          {items.map((item) => (
            <article className="shipping-card" key={item.id}>
              <header className="shipping-card__header">
                <div className="shipping-card__title">
                  <span className="shipping-card__icon"><Truck /></span>
                  <div><h3>{item.name}</h3><small dir="ltr">{item.code}</small></div>
                </div>
                <div className="shipping-card__status">
                  <Badge value={item.isActive ? "فعال" : "غیرفعال"} tone={item.isActive ? "success" : "neutral"} />
                  <button
                    type="button"
                    className={`switch ${item.isActive ? "is-on" : ""}`}
                    role="switch"
                    aria-checked={item.isActive}
                    aria-label={`${item.isActive ? "غیرفعال" : "فعال"} کردن ${item.name}`}
                    disabled={toggleActive.isPending}
                    onClick={() => toggleActive.mutate(item)}
                  ><span /></button>
                </div>
              </header>
              <p>{item.description || "برای این روش ارسال توضیحی ثبت نشده است."}</p>
              <div className="shipping-card__metrics">
                <div><span>هزینه پایه</span><strong>{item.basePrice === 0 ? "رایگان" : money(item.basePrice)}</strong></div>
                <div><span>ارسال رایگان</span><strong>{item.freeShippingThreshold ? money(item.freeShippingThreshold) : "ندارد"}</strong></div>
                <div><span>تحویل</span><strong>{number(item.estimatedMinDays)} تا {number(item.estimatedMaxDays)} روز</strong></div>
              </div>
              <footer className="shipping-card__footer">
                <span className="text-[11px] text-slate-500">ترتیب نمایش: {number(item.displayOrder)}</span>
                <div className="flex gap-1">
                  <IconButton label="ویرایش" onClick={() => open(item)}><Edit3 /></IconButton>
                  <IconButton label="حذف" onClick={() => setDeleting(item)}><Trash2 /></IconButton>
                </div>
              </footer>
            </article>
          ))}
        </div>
      )}

      <Modal open={Boolean(editing)} title={editing === "new" ? "تعریف روش ارسال" : "ویرایش روش ارسال"} onClose={() => setEditing(null)} wide>
        <form className="stack" onSubmit={handleSubmit((form) => save.mutate(form))} noValidate>
          <FormErrorSummary errors={[errors.code?.message, errors.name?.message, errors.basePrice?.message, errors.freeShippingThreshold?.message, errors.estimatedMinDays?.message, errors.estimatedMaxDays?.message, errors.displayOrder?.message]} />

          <FormSection title="مشخصات روش" description="نام برای مشتری نمایش داده می‌شود و کد فقط برای شناسه داخلی و فنی است.">
            <div className="form-grid">
              <Input label="نام روش ارسال" error={errors.name?.message} hint="مثال: پست پیشتاز یا ارسال سریع تهران" {...register("name")} />
              <Input label="کد روش" dir="ltr" error={errors.code?.message} hint="فقط حروف انگلیسی، عدد، خط تیره و زیرخط." {...register("code", { onChange: (event) => setValue("code", cleanCode(String(event.target.value)), { shouldDirty: true, shouldValidate: true }) })} />
              <Textarea className="span-2" label="توضیحات" rows={4} error={errors.description?.message} hint="شرایط مهم ارسال را کوتاه و روشن توضیح دهید." {...register("description")} />
            </div>
          </FormSection>

          <FormSection title="محاسبه هزینه" description="هزینه پایه و آستانه‌ای که بعد از آن ارسال رایگان می‌شود.">
            <div className="form-grid">
              <Input label="هزینه پایه (تومان)" type="number" min="0" error={errors.basePrice?.message} hint="صفر یعنی این روش همیشه رایگان است." {...register("basePrice", { valueAsNumber: true })} />
              <Input label="ارسال رایگان از مبلغ (تومان)" type="number" min="1" error={errors.freeShippingThreshold?.message} hint="خالی یعنی آستانه ارسال رایگان ندارد." {...register("freeShippingThreshold", { setValueAs: (value) => value === "" ? null : Number(value) })} />
            </div>
          </FormSection>

          <FormSection title="زمان تحویل" description="بازه زمانی واقع‌بینانه‌ای که مشتری در Checkout مشاهده می‌کند.">
            <div className="form-grid form-grid--3">
              <Input label="حداقل روز" type="number" min="0" error={errors.estimatedMinDays?.message} {...register("estimatedMinDays", { valueAsNumber: true })} />
              <Input label="حداکثر روز" type="number" min="0" error={errors.estimatedMaxDays?.message} {...register("estimatedMaxDays", { valueAsNumber: true })} />
              <Input label="ترتیب نمایش" type="number" min="0" error={errors.displayOrder?.message} hint="عدد کمتر زودتر نمایش داده می‌شود." {...register("displayOrder", { valueAsNumber: true })} />
            </div>
          </FormSection>

          <FormSection title="وضعیت نمایش" description="روش غیرفعال برای سفارش جدید نمایش داده نمی‌شود، اما سوابق سفارش‌های قبلی حفظ می‌شود.">
            <Controller control={control} name="isActive" render={({ field }) => <Switch label="روش ارسال فعال" checked={field.value} onChange={field.onChange} />} />
          </FormSection>

          <div className="shipping-preview">
            <strong>{name || "عنوان روش ارسال"}</strong>
            <span>{basePrice === 0 ? "ارسال رایگان" : money(basePrice || 0)} · تحویل حدود {number(minDays || 0)} تا {number(maxDays || 0)} روز{threshold ? ` · رایگان برای خرید بالای ${money(threshold)}` : ""} · {active ? "قابل انتخاب" : "غیرفعال"}</span>
          </div>

          {save.error ? <Notice tone="warning">{save.error.message}</Notice> : null}
          <div className="form-actions">
            <Button type="button" variant="ghost" onClick={() => setEditing(null)}>انصراف</Button>
            <Button type="submit" busy={save.isPending}>ذخیره روش ارسال</Button>
          </div>
        </form>
      </Modal>

      <Confirm open={Boolean(deleting)} title="حذف روش ارسال" description={`روش «${deleting?.name || ""}» حذف شود؟ اگر سفارش قبلی از این روش استفاده کرده باشد Backend به‌جای حذف فیزیکی، آن را غیرفعال می‌کند تا تاریخچه سفارش حفظ شود.`} confirmLabel="حذف روش" busy={remove.isPending} onClose={() => setDeleting(null)} onConfirm={() => deleting && remove.mutate(deleting.id)} />
    </>
  );
}
